import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Info } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

// ---------------------------------------------------------------------------
// Approved price amounts — DO NOT CHANGE. Only the surrounding words are
// localised (see t.pricing in translations.js).
// ---------------------------------------------------------------------------
const CURR = { CH: "CHF", DE: "€", AT: "€" };
const COUNTRY_CODES = ["CH", "DE", "AT"];

const EVENT_AMOUNTS = {
  tryIt: { CH: "990", DE: "990", AT: "790" },
  makeItYours: { CH: "1'390", DE: "1'390", AT: "1'090" },
  weTakeCare: { CH: "1'990", DE: "1'990", AT: "1'490" },
};

const ADDON_AMOUNTS = [
  { CH: "250", DE: "250", AT: "200" },
  { CH: "750", DE: "750", AT: "590" },
];

// CH: "ab <amount>", DE/AT: on request
const OUTDOOR_AMOUNTS = ["420", "150", "150", "570"];

const BRANDED_AMOUNTS = [
  { CH: "590", DE: "590", AT: "490" },
  { CH: "490", DE: "490", AT: "390" },
  { CH: "390", DE: "390", AT: "290" },
];

const CAMPAIGN_AMOUNTS = [
  { CH: "570", DE: "570", AT: "450" },
  { CH: "2'790", DE: "2'790", AT: "2'190" },
  { CH: "5'999", DE: "5'999", AT: "4'799" },
];

const WE_TAKE_BRANDING_AMOUNTS = { CH: "2'490", DE: "2'490", AT: "1'990" };

const Card = ({ children, highlight = false, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.5 }}
    data-testid={testid}
    className={`rounded-3xl p-7 md:p-8 border ${
      highlight
        ? "border-[#0055FF]/30 bg-white shadow-xl shadow-[#0055FF]/5"
        : "border-slate-200/70 bg-white shadow-sm"
    }`}
  >
    {children}
  </motion.div>
);

const Pricing = () => {
  const { t } = useLanguage();
  const [country, setCountry] = useState("CH");
  const p = t.pricing;
  if (!p) return null;

  const from = (amt) => `${p.from} ${CURR[country]} ${amt}`;
  const plain = (amt) => `${CURR[country]} ${amt}`;
  const org = (amt) => `${p.orgFrom} ${CURR[country]} ${amt}`;
  const monthly = (amt) => `${CURR[country]} ${amt} ${p.perMonth}`;
  const outdoorPrice = (i) =>
    country === "CH" ? from(OUTDOOR_AMOUNTS[i]) : p.onRequest;

  const eventOffers = [
    { key: "tryIt", testid: "pricing-event-try-it", price: from(EVENT_AMOUNTS.tryIt[country]) },
    { key: "makeItYours", testid: "pricing-event-make-it-yours", price: from(EVENT_AMOUNTS.makeItYours[country]) },
    { key: "weTakeCare", testid: "pricing-event-we-take-care", price: org(EVENT_AMOUNTS.weTakeCare[country]) },
  ];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-20 md:py-28 bg-[#F8FAFC] scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0055FF]">
            {p.kicker}
          </div>
          <h2 className="mt-3 font-heading text-3xl md:text-5xl font-bold tracking-tight text-neutral-900">
            {p.title}
          </h2>
          <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
            {p.subtitle}
          </p>
        </div>

        <div
          role="radiogroup"
          aria-label={p.countryAria}
          data-testid="country-selector"
          className="mt-8 inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm"
        >
          {COUNTRY_CODES.map((code) => (
            <button
              key={code}
              role="radio"
              aria-checked={country === code}
              data-testid={`country-${code}`}
              onClick={() => setCountry(code)}
              className={`px-4 md:px-5 py-2 text-sm font-semibold rounded-full transition-colors ${
                country === code
                  ? "bg-neutral-900 text-white"
                  : "text-neutral-600 hover:text-neutral-900"
              }`}
            >
              {p.countries[code]}
            </button>
          ))}
        </div>

        {/* ======================= EVENTS ======================= */}
        <div id="pricing-events" className="mt-14 md:mt-20 scroll-mt-24">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-neutral-900">
              {p.eventsTitle}
            </h3>
            <div className="text-xs uppercase tracking-widest font-semibold text-neutral-500">
              {p.eventsTag}
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-3 gap-5 md:gap-6">
            {eventOffers.map(({ key, testid, price }, i) => {
              const o = p.offers[key];
              return (
                <Card key={key} highlight={i === 1} testid={testid}>
                  <div className="text-xs font-semibold tracking-widest text-[#0055FF]">
                    {o.label}
                  </div>
                  <h4 className="mt-2 font-heading text-xl font-bold text-neutral-900">
                    {o.title}
                  </h4>
                  <div className="mt-4 text-2xl font-bold text-neutral-900">
                    {price}
                  </div>
                  {o.desc && (
                    <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{o.desc}</p>
                  )}
                  {o.items && (
                    <ul className="mt-5 space-y-2.5">
                      {o.items.map((it, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-neutral-700">
                          <Check className="w-4 h-4 text-[#0055FF] mt-0.5 shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {o.note && (
                    <p className="mt-5 text-xs text-neutral-500 italic leading-relaxed">{o.note}</p>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Add-ons */}
          <div
            data-testid="event-addons"
            className="mt-8 rounded-3xl border border-slate-200/70 bg-white p-6 md:p-7"
          >
            <div className="text-xs font-semibold tracking-widest text-neutral-500">
              {p.addonsTitle}
            </div>
            <div className="mt-4 divide-y divide-slate-100">
              {p.addons.map((label, i) => (
                <div
                  key={label}
                  className="py-3 flex items-center justify-between gap-4 text-sm"
                >
                  <span className="text-neutral-700">{label}</span>
                  <span className="font-semibold text-neutral-900 tabular-nums">
                    {plain(ADDON_AMOUNTS[i][country])}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-6 text-xs font-semibold tracking-widest text-neutral-500">
              {p.outdoorTitle}
            </div>
            <div className="mt-3 divide-y divide-slate-100">
              {p.outdoor.map((label, i) => (
                <div
                  key={label}
                  className="py-3 flex items-center justify-between gap-4 text-sm"
                >
                  <span className="text-neutral-700">{label}</span>
                  <span className="font-semibold text-neutral-900 tabular-nums">
                    {outdoorPrice(i)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Event inspiration & individual requests (Task 4) */}
          <div
            data-testid="event-inspiration"
            className="mt-6 rounded-3xl border border-slate-200/70 bg-white p-6 md:p-7 grid md:grid-cols-2 gap-6 md:gap-10"
          >
            <div>
              <h4 className="font-heading text-base md:text-lg font-bold text-neutral-900">
                {p.inspiration.title}
              </h4>
              <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
                {p.inspiration.body}
              </p>
            </div>
            <div>
              <h4 className="font-heading text-base md:text-lg font-bold text-neutral-900">
                {p.custom.title}
              </h4>
              <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
                {p.custom.body}
              </p>
            </div>
          </div>

          {/* Founding Client Season */}
          {country === "CH" && (
            <div
              data-testid="founding-client-season"
              className="mt-6 rounded-3xl border border-[#FF7A00]/30 bg-[#FFF7EC] p-6 md:p-7"
            >
              <div className="text-xs font-semibold tracking-widest text-[#FF7A00]">
                {p.founding.kicker}
              </div>
              <h4 className="mt-2 font-heading text-xl font-bold text-neutral-900">
                {p.founding.title}
              </h4>
              <p className="mt-3 text-sm text-neutral-700 leading-relaxed">
                <span className="font-semibold">{p.founding.body1}</span>{" "}
                {p.founding.body2}
              </p>
            </div>
          )}

          {/* Payment Terms */}
          <div
            data-testid="payment-terms"
            className="mt-6 flex items-start gap-3 text-sm text-neutral-600 leading-relaxed max-w-3xl"
          >
            <Info className="w-4 h-4 text-neutral-400 mt-0.5 shrink-0" />
            <span>
              <span className="font-semibold text-neutral-800">{p.payment.label}</span>{" "}
              {p.payment.text}
            </span>
          </div>
        </div>

        {/* ======================= BRANDING ======================= */}
        <div id="pricing-branding" className="mt-16 md:mt-24 scroll-mt-24">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-neutral-900">
              {p.brandingTitle}
            </h3>
            <div className="text-xs uppercase tracking-widest font-semibold text-neutral-500">
              {p.brandingTag}
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-3 gap-5 md:gap-6">
            {/* BRANDED FOOTPOOL */}
            <Card testid="pricing-branded-footpool">
              <div className="text-xs font-semibold tracking-widest text-[#0055FF]">
                {p.branded.label}
              </div>
              <h4 className="mt-2 font-heading text-xl font-bold text-neutral-900">
                {p.branded.title}
              </h4>
              <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 divide-y divide-slate-100">
                {p.branded.durations.map((label, i) => (
                  <div key={label} className="flex items-center justify-between px-3 py-2.5 text-sm">
                    <span className="text-neutral-700">{label}</span>
                    <span className="font-semibold text-neutral-900 tabular-nums">
                      {monthly(BRANDED_AMOUNTS[i][country])}
                    </span>
                  </div>
                ))}
              </div>
              <ul className="mt-5 space-y-2.5">
                {p.branded.items.map((it, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-neutral-700">
                    <Check className="w-4 h-4 text-[#0055FF] mt-0.5 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* CAMPAIGN FOOTPOOL */}
            <Card highlight testid="pricing-campaign-footpool">
              <div className="text-xs font-semibold tracking-widest text-[#0055FF]">
                {p.campaign.label}
              </div>
              <h4 className="mt-2 font-heading text-xl font-bold text-neutral-900">
                {p.campaign.title}
              </h4>
              <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                {p.campaign.desc}
              </p>
              <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 divide-y divide-slate-100">
                {p.campaign.rates.map((label, i) => (
                  <div key={label} className="flex items-center justify-between px-3 py-2.5 text-sm">
                    <span className="text-neutral-700">{label}</span>
                    <span className="font-semibold text-neutral-900 tabular-nums">
                      {from(CAMPAIGN_AMOUNTS[i][country])}
                    </span>
                  </div>
                ))}
              </div>
              <ul className="mt-5 space-y-2 text-sm text-neutral-700">
                {p.campaign.items.map((it, idx) => (
                  <li key={idx} className="flex gap-2">
                    <Check className="w-4 h-4 text-[#0055FF] mt-0.5 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* WE TAKE CARE OF IT (Branding) */}
            <Card testid="pricing-branding-we-take-care">
              <div className="text-xs font-semibold tracking-widest text-[#0055FF]">
                {p.weTakeBrand.label}
              </div>
              <h4 className="mt-2 font-heading text-xl font-bold text-neutral-900">
                {p.weTakeBrand.title}
              </h4>
              <div className="mt-4 text-2xl font-bold text-neutral-900">
                {org(WE_TAKE_BRANDING_AMOUNTS[country])}
              </div>
              <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                {p.weTakeBrand.desc}
              </p>
              <p className="mt-4 text-xs text-neutral-500 italic leading-relaxed">
                {p.weTakeBrand.note}
              </p>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
